import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Users, LogIn, CreditCard, Zap, Check, MapPin } from "lucide-react";
import Navbar from "../components/Navbar";
import VehiclePanel from "../components/Sidebar";
import MapView from "../components/MapView";
import AICopilot from "../components/AICopilot";
import { useVehicleTracking } from "../hooks/useVehicleTracking";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";

const DEMO_PLANS = [
  { id:"plan_50",  calls:"50",  price:"₹300", gst:"₹354 incl. GST" },
  { id:"plan_100", calls:"100", price:"₹550", gst:"₹649 incl. GST" },
  { id:"plan_150", calls:"150", price:"₹750", gst:"₹885 incl. GST" },
  { id:"plan_200", calls:"200", price:"₹900", gst:"₹1,062 incl. GST", best:true },
];

/* ── Map lock overlay — shown when user is not logged in ── */
function MapLockOverlay({ onSignIn }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div style={{
      position:"absolute", inset:0, zIndex:15,
      display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      {/* Dark blur backdrop */}
      <div style={{
        position:"absolute", inset:0,
        background:"rgba(15,23,42,0.72)",
        backdropFilter:"blur(4px)",
      }} />

      {/* Card */}
      <motion.div
        initial={{ scale:.94, opacity:0, y:20 }}
        animate={{ scale:1,   opacity:1, y:0 }}
        transition={{ duration:0.28, ease:"easeOut" }}
        style={{
          position:"relative", zIndex:1,
          width:"100%", maxWidth:420, margin:16,
          background:"linear-gradient(145deg,#1E1B4B,#0F172A)",
          borderRadius:20, overflow:"hidden",
          border:"1px solid rgba(99,102,241,0.3)",
          boxShadow:"0 32px 80px rgba(0,0,0,0.5), 0 0 40px rgba(99,102,241,0.12)",
        }}
      >
        {/* Gradient top bar */}
        <div style={{ height:4, background:"linear-gradient(90deg,#6366F1,#8B5CF6,#A78BFA)" }} />

        <div style={{ padding:"24px 24px 22px" }}>
          {/* Header */}
          <div style={{ textAlign:"center", marginBottom:18 }}>
            <div style={{ fontSize:19, fontWeight:800, color:"#fff", letterSpacing:-0.3 }}>
              Choose a Plan to Get Started
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", marginTop:6, lineHeight:1.6 }}>
              Sign up → get <strong style={{ color:"#A78BFA" }}>2 free API calls</strong> instantly →
              activate your plan when ready
            </div>
          </div>

          {/* Clickable plan cards */}
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
            {DEMO_PLANS.map(p => (
              <button
                key={p.id}
                onClick={onSignIn}
                onMouseEnter={() => setHovered(p.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  width:"100%", border:"none", cursor:"pointer", textAlign:"left",
                  background: hovered === p.id
                    ? (p.best ? "rgba(99,102,241,0.35)" : "rgba(255,255,255,0.10)")
                    : (p.best ? "rgba(99,102,241,0.20)" : "rgba(255,255,255,0.05)"),
                  border:`1.5px solid ${p.best ? "rgba(99,102,241,0.55)" : (hovered === p.id ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.09)")}`,
                  borderRadius:12, padding:"12px 14px",
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  transition:"all 0.15s",
                  transform: hovered === p.id ? "translateX(3px)" : "translateX(0)",
                }}
              >
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  {p.best && (
                    <div style={{
                      fontSize:9, fontWeight:700, color:"#fff",
                      background:"#6366F1", borderRadius:4, padding:"2px 6px",
                      letterSpacing:0.5,
                    }}>BEST</div>
                  )}
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>
                      {p.calls} API calls
                    </div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:1 }}>
                      {p.gst}
                    </div>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ fontSize:15, fontWeight:800, color: p.best ? "#A78BFA" : "#818CF8" }}>
                    {p.price}
                  </div>
                  <ArrowRight size={14} color={p.best ? "#A78BFA" : "rgba(255,255,255,0.3)"}
                    style={{ transition:"transform 0.15s", transform: hovered === p.id ? "translateX(2px)" : "none" }}
                  />
                </div>
              </button>
            ))}
          </div>

          {/* Free trial note */}
          <div style={{
            background:"rgba(99,102,241,0.1)",
            border:"1px solid rgba(99,102,241,0.25)",
            borderRadius:10, padding:"10px 14px",
            display:"flex", alignItems:"center", gap:10,
          }}>
            <Zap size={14} color="#818CF8" style={{ flexShrink:0 }} />
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", lineHeight:1.5 }}>
              <strong style={{ color:"#A78BFA" }}>2 free calls included</strong> on every new account.
              Click any plan above to sign up — you won't be charged until you choose to activate.
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Sign-in required prompt ───────────────────────────── */
function SignInRequired({ onClose, onGoToAuth }) {
  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{
        position:"fixed", inset:0, zIndex:99999,
        background:"rgba(17,24,39,0.55)",
        display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale:.95, y:16, opacity:0 }}
        animate={{ scale:1,   y:0,  opacity:1 }}
        exit={{    scale:.95, y:16, opacity:0 }}
        transition={{ duration:0.18, ease:"easeOut" }}
        style={{
          width:"100%", maxWidth:380,
          background:"#fff", borderRadius:16,
          overflow:"hidden",
          boxShadow:"0 24px 64px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ height:4, background:"#4F46E5" }} />
        <div style={{ padding:"28px 28px 24px", textAlign:"center" }}>
          <div style={{
            width:52, height:52, borderRadius:14,
            background:"#EFF6FF", border:"1px solid #BFDBFE",
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 18px",
          }}>
            <LogIn size={22} color="#4F46E5" />
          </div>
          <div style={{ fontSize:18, fontWeight:700, color:"#111827", marginBottom:6 }}>
            Sign in to add vehicles
          </div>
          <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.6, marginBottom:14 }}>
            Sign up → get <strong style={{ color:"#4F46E5" }}>2 free API calls</strong> → choose a plan when ready.
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:16 }}>
            {DEMO_PLANS.map(p => (
              <button
                key={p.id}
                onClick={onGoToAuth}
                style={{
                  width:"100%", border:"none", cursor:"pointer", textAlign:"left",
                  background: p.best ? "#EEF2FF" : "#F8FAFC",
                  border:`1.5px solid ${p.best ? "#C7D2FE" : "#E5E7EB"}`,
                  borderRadius:10, padding:"10px 14px",
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  transition:"all 0.15s",
                }}
                onMouseOver={e => { e.currentTarget.style.transform="translateX(3px)"; e.currentTarget.style.borderColor=p.best?"#818CF8":"#D1D5DB"; }}
                onMouseOut={e  => { e.currentTarget.style.transform="translateX(0)";   e.currentTarget.style.borderColor=p.best?"#C7D2FE":"#E5E7EB"; }}
              >
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  {p.best && (
                    <div style={{ fontSize:9, fontWeight:700, color:"#fff", background:"#6366F1", borderRadius:4, padding:"2px 6px" }}>
                      BEST
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{p.calls} API calls</div>
                    <div style={{ fontSize:10, color:"#9CA3AF", marginTop:1 }}>{p.gst}</div>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ fontSize:14, fontWeight:800, color: p.best ? "#4F46E5" : "#374151" }}>{p.price}</div>
                  <ArrowRight size={13} color={p.best ? "#6366F1" : "#9CA3AF"} />
                </div>
              </button>
            ))}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <button
              onClick={onGoToAuth}
              style={{
                width:"100%", height:44, borderRadius:9,
                background:"linear-gradient(135deg,#6366F1,#7C3AED)", border:"none",
                color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer",
                boxShadow:"0 4px 16px rgba(99,102,241,0.45)",
                transition:"all 0.2s",
              }}
              onMouseOver={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(99,102,241,0.6)"; }}
              onMouseOut={e  => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(99,102,241,0.45)"; }}
            >
              Sign Up & Start Tracking
            </button>
            <button
              onClick={onClose}
              style={{
                width:"100%", height:36, borderRadius:9,
                background:"none", border:"1px solid #E5E7EB",
                color:"#6B7280", fontSize:13, fontWeight:500, cursor:"pointer",
                transition:"all 0.15s",
              }}
              onMouseOver={e => e.currentTarget.style.background="#F9FAFB"}
              onMouseOut={e  => e.currentTarget.style.background="none"}
            >
              Continue browsing demo
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function DemoPage() {
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [focusedToll,      setFocusedToll]      = useState(null);
  const [showCopilot,      setShowCopilot]       = useState(false);
  const [searchQuery,      setSearchQuery]       = useState("");

  const { session } = useAuth();
  const navigate    = useNavigate();
  const isMobile    = useIsMobile();

  // Demo: local state only, no API call counting
  const {
    vehicles, selectedVehicle,
    removeVehicle, selectVehicle, setSelectedVehicle,
  } = useVehicleTracking();

  function handleTollClick(toll) {
    setFocusedToll(prev => prev?.id === toll.id ? null : toll);
  }

  // Always route to auth — AuthPage redirects to /app if already logged in
  function handleAddVehicleClick() {
    navigate("/auth");
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", overflow:"hidden", background:"#F7F8FA" }}>
      <Toaster
        position="top-center"
        toastOptions={{
          style:{
            background:"#fff", color:"#111827",
            border:"1px solid #E5E7EB", borderRadius:10,
            boxShadow:"0 4px 16px rgba(0,0,0,0.08)", fontSize:13,
          },
          success:{ iconTheme:{ primary:"#16A34A", secondary:"#fff" } },
          error:  { iconTheme:{ primary:"#DC2626", secondary:"#fff" } },
        }}
      />

      {/* Demo banner */}
      <div style={{
        background:"#4F46E5",
        paddingTop: isMobile ? "max(8px, var(--sat, 0px))" : "9px",
        paddingBottom: isMobile ? "8px" : "9px",
        paddingLeft: isMobile ? "14px" : "20px",
        paddingRight: isMobile ? "14px" : "20px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        flexShrink:0, gap:8,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            style={{
              display:"flex", alignItems:"center", justifyContent:"center",
              background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)",
              borderRadius:7, width:30, height:30, cursor:"pointer", flexShrink:0,
              color:"#fff", padding:0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"rgba(255,255,255,0.9)" }}>
            <Users size={13} color="#BFDBFE" style={{ flexShrink:0 }} />
            {isMobile ? (
              <span style={{ fontWeight:600, color:"#fff", whiteSpace:"nowrap" }}>Live Demo</span>
            ) : (
              <span>
                <strong style={{ color:"#fff" }}>Live Demo</strong> — You're viewing real FASTag data.
                Sign up to track your own fleet in real time.
              </span>
            )}
          </div>
        </div>
        <div style={{ display:"flex", gap:6, flexShrink:0 }}>
          {!isMobile && (
            <Link to="/auth" style={{
              display:"flex", alignItems:"center", gap:6,
              background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)",
              borderRadius:7, padding:"6px 14px",
              fontSize:12, fontWeight:600, color:"#fff", textDecoration:"none",
            }}>
              Sign In
            </Link>
          )}
          <Link to="/auth" style={{
            display:"flex", alignItems:"center", gap:5,
            background:"#ffffff", borderRadius:7, padding:"6px 12px",
            fontSize:12, fontWeight:700, color:"#4F46E5", textDecoration:"none",
            whiteSpace:"nowrap",
          }}>
            Get Started <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {/* Navbar */}
      <Navbar
        vehicles={vehicles}
        onSearch={setSearchQuery}
        searchQuery={searchQuery}
        extraRight={
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            {!isMobile && (
              <Link to="/auth" style={{
                display:"flex", alignItems:"center",
                background:"none", border:"1px solid #E5E7EB", borderRadius:8,
                padding:"0 14px", height:36,
                fontSize:13, fontWeight:500, color:"#374151", textDecoration:"none",
              }}>
                Sign In
              </Link>
            )}
            <Link to="/auth" style={{
              display:"flex", alignItems:"center", gap:5,
              background:"#4F46E5", borderRadius:8,
              padding:"0 12px", height:36,
              fontSize:13, fontWeight:600, color:"#fff", textDecoration:"none",
              whiteSpace:"nowrap",
            }}>
              Get Started {!isMobile && <ArrowRight size={13} />}
            </Link>
          </div>
        }
      />

      {/* Main workspace */}
      <main style={{ flex:1, display:"flex", overflow:"hidden" }}>
        <div style={{ flex:1, position:"relative", overflow:"hidden" }}>
          {/* Map */}
          <div style={{
            width:"100%", height:"100%",
            filter:      !session ? "blur(4px)" : "none",
            transform:   !session ? "scale(1.04)" : "scale(1)",
            transition:  "filter 0.3s, transform 0.3s",
            pointerEvents: !session ? "none" : "auto",
          }}>
            <MapView
              vehicles={vehicles}
              selectedVehicle={selectedVehicle}
              onSelectVehicle={v => { selectVehicle(v); setFocusedToll(null); }}
              focusedToll={focusedToll}
              onTollClick={handleTollClick}
            />
          </div>
          {/* Lock overlay for unauthenticated visitors */}
          {!session && (
            <MapLockOverlay onSignIn={() => navigate("/auth")} />
          )}
        </div>

        {/* Sidebar — hidden on mobile (lock overlay is the CTA) */}
        {!isMobile && (
          <div style={{ width:340, flexShrink:0, borderLeft:"1px solid #EBEBEB", overflow:"hidden" }}>
            <VehiclePanel
              vehicles={vehicles}
              selectedVehicle={selectedVehicle}
              onSelectVehicle={v => v ? selectVehicle(v) : setSelectedVehicle(null)}
              onRemoveVehicle={removeVehicle}
              onAddVehicle={handleAddVehicleClick}
              onTollClick={handleTollClick}
              activeTollId={focusedToll?.id}
              searchQuery={searchQuery}
            />
          </div>
        )}
      </main>

      <AnimatePresence>
        {showSignInPrompt && (
          <SignInRequired
            key="sign-in-required"
            onClose={() => setShowSignInPrompt(false)}
            onGoToAuth={() => navigate("/auth")}
          />
        )}
      </AnimatePresence>

      <AICopilot
        vehicles={vehicles}
        isOpen={showCopilot}
        onToggle={() => setShowCopilot(v => !v)}
      />
    </div>
  );
}
